import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Chart as ChartJS, ArcElement, Title as ChartTitle, Tooltip, Legend } from 'chart.js';
import {
  PageSection,
  ControlContainerRow,
  ControlContainer,
  Subtitle,
  StyledCard,
} from 'common/styles/page';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';
import CategorySelect from 'common/components/CategorySelect';
import { Doughnut } from 'react-chartjs-2';

import { useApiPortalV1AssessmentsResultsListQuery } from 'areas/assessment-schedule/api';
import { hexToRgb } from 'common/styles/colors';
import { AggregateType } from 'common/constants/AggregateType';
import {
  getSelectedAssessmentPeriod,
  getSelectedAssessmentPeriodCategories,
  getSelectedAssessmentPeriodId,
} from 'common/selectors/auth';

ChartJS.register(ArcElement, ChartTitle, Tooltip, Legend);

interface AnalyticsDemographicsPieProps {}

const AnalyticsDemographicsPie: React.FC<AnalyticsDemographicsPieProps> = ({}) => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const assessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const assessmentPeriodCategories = useSelector(
    getSelectedAssessmentPeriodCategories
  ) as any[];
  const assessmentPeriodName = assessmentPeriod?.name;

  const { data: data_by_category } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_OVERALL,
    assessmentPeriodId: assessmentPeriodId,
  });

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { setPagePrompts } = usePageCopilotPrompts();

  const [chartData, setChartData] = useState({
    labels: [],
    ids: [],
    datasets: [
      {
        label: 'Scores',
        data: [],
        scores: [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
    ],
  });

  useEffect(() => {
    if (!assessmentPeriodCategories || assessmentPeriodCategories.length === 0) {
      setSelectedCategory(null);
      return;
    }

    const selectedCategoryExists = assessmentPeriodCategories.some(
      category => category.id === selectedCategory
    );

    if (selectedCategoryExists) return;

    const primaryCategory = assessmentPeriodCategories.find(
      category => category.priority === 'primary'
    );

    setSelectedCategory(primaryCategory?.id || assessmentPeriodCategories[0]?.id || null);
  }, [assessmentPeriodCategories, selectedCategory]);

  useEffect(() => {
    if (data_by_category && selectedCategory) {
      const filteredData = data_by_category[0]?.result.aggregate_scores.filter(
        (score: { category_id: string }) => score.category_id === selectedCategory
      );

      const backgroundColors = filteredData.map((score: { color: string }) =>
        hexToRgb(score.color, 0.6)
      );
      const borderColors = filteredData.map((score: { color: string }) =>
        hexToRgb(score.color, 0.8)
      );

      const updatedChartData = {
        labels: filteredData.map((score: { option_name: string }) => score.option_name),
        ids: filteredData.map((score: { option_id: string }) => score.option_id),
        datasets: [
          {
            label: 'Scores',
            scores: filteredData.map((score: { overall_score: number }) =>
              score.overall_score.toFixed(1)
            ),
            data: filteredData.map((score: { average_n: number }) => score.average_n),
            backgroundColor: backgroundColors,
            borderColor: borderColors,
          },
        ],
      };
      setChartData(updatedChartData);
    }
  }, [data_by_category, selectedCategory]);

  useEffect(() => {
    const categoryLabel =
      assessmentPeriodCategories.find(category => category.id === selectedCategory)?.name ||
      'Selected Category';

    setPagePrompts([
      {
        title: 'Demographic Distribution',
        displayText: `Summarize the demographic distribution for ${categoryLabel}.`,
        prompt:
          `Review the demographic summary for ${categoryLabel} and explain which groups show the strongest participation, scores, or representation. Highlight the most important differences and what they mean for inclusion and engagement.`,
      },
      {
        title: 'Group Score Gaps',
        displayText: `Identify score gaps within ${categoryLabel}.`,
        prompt:
          `Identify the most significant score gaps between demographic groups for ${categoryLabel}. Explain which groups are underperforming, which are overperforming, and why those gaps should matter to leaders.`,
      },
      {
        title: 'Equity Actions',
        displayText: `Recommend equity actions for ${categoryLabel}.`,
        prompt:
          `Based on the demographic distribution for ${categoryLabel}, recommend actions leaders should take to improve equity, participation, and support for underrepresented groups.`,
      },
    ]);

    return () => setPagePrompts([]);
  }, [setPagePrompts, assessmentPeriodCategories, selectedCategory]);

  return (
    <>
      <PageSection
        padright
        width="100%"
        display="flex"
        alignItems="flex-start"
        gap="10px"
        style={{ height: '100%' }}
      >
        <StyledCard>
          <ControlContainerRow padbottom="10px" padright="10px" padleft="20px" padtop="10px">
            <ControlContainer justify="left">
              <Subtitle>{`Survey Respondents (${assessmentPeriodName})`}</Subtitle>
            </ControlContainer>
            <ControlContainer justify="right">
              <CategorySelect
                categories={assessmentPeriodCategories as any}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                placeholder="Select Category"
                width="250px"
              />
            </ControlContainer>
          </ControlContainerRow>

          <PageSection
            padbottom
            padleft
            padright
            display="flex"
            flexDirection="row"
            style={{ width: '100%', height: '450px' }}
          >
            <Doughnut
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '60%',
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      padding: 30,
                      font: {
                        family: 'Poppins, sans-serif',
                        size: 12,
                      },
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: (tooltipItem: any) => {
                        const index = tooltipItem.dataIndex;
                        const nValue = chartData.datasets[0].data[index];
                        const score = chartData.datasets[0].scores[index];
                        const total = chartData.datasets[0].data.reduce(
                          (acc: number, val: number) => acc + val,
                          0
                        );
                        const percentage = total > 0 ? ((nValue / total) * 100).toFixed(1) : '0.0';
                        return `${nValue} Respondents (${percentage}%) | Overall Score: ${score}`;
                      },
                    },
                  },
                },
              }}
              style={{ height: '100%' }}
            />
          </PageSection>
        </StyledCard>
      </PageSection>
    </>
  );
};

export default AnalyticsDemographicsPie;

